/** Enrollment gap vs Target (0.7): actual cumulative enrolled minus target, as a signed area. */
import { useCallback } from "react";
import * as d3 from "d3";
import {
  COLORS,
  useD3Chart,
  drawTimeAxes,
  parseMonth,
  fmtMonthLong,
  FONT,
  type Margin,
} from "./chartBase";
import type { EnrollmentGapChartProps } from "./onboardingPlots.types";

export default function EnrollmentGapChart({
  rows,
  height = 300,
}: EnrollmentGapChartProps) {
  const draw = useCallback(
    (svg, tip, W) => {
      const H = height;
      const m: Margin = { top: 16, right: 16, bottom: 34, left: 40 };

      const d = rows
        .filter(
          (r) =>
            r.enrolledCumulative != null &&
            r.scenarios.target.enrolledCum != null,
        )
        .map((r) => ({
          d: parseMonth(r.date) as Date,
          v:
            (r.enrolledCumulative as number) -
            (r.scenarios.target.enrolledCum as number),
        }));
      if (!d.length) return;

      const x = d3
        .scaleTime()
        .domain(
          d3.extent(rows, (r) => parseMonth(r.date) as Date) as [Date, Date],
        )
        .range([m.left, W - m.right]);
      const ext = d3.extent(d, (r) => r.v) as [number, number];
      const minGap = Math.min(0, ext[0]);
      const maxGap = Math.max(0, ext[1]);
      const pad =
        minGap === maxGap ? 1 : Math.max((maxGap - minGap) * 0.08, 0.5);
      const y = d3
        .scaleLinear()
        .domain([minGap - pad, maxGap + pad])
        .nice()
        .range([H - m.bottom, m.top]);
      drawTimeAxes(svg, W, H, m, x, y, undefined, true);

      svg
        .append("line")
        .attr("x1", m.left)
        .attr("x2", W - m.right)
        .attr("y1", y(0))
        .attr("y2", y(0))
        .attr("stroke", "#b9ccd4");
      svg
        .append("path")
        .datum(d)
        .attr("fill", COLORS.red)
        .attr("opacity", 0.18)
        .attr(
          "d",
          d3
            .area<any>()
            .x((r) => x(r.d))
            .y0(y(0))
            .y1((r) => y(r.v)),
        );
      svg
        .append("path")
        .datum(d)
        .attr("fill", "none")
        .attr("stroke", COLORS.red)
        .attr("stroke-width", 2.5)
        .attr("stroke-linecap", "round")
        .attr("stroke-linejoin", "round")
        .attr(
          "d",
          d3
            .line<any>()
            .x((r) => x(r.d))
            .y((r) => y(r.v)),
        );
      svg
        .selectAll("circle")
        .data(d)
        .join("circle")
        .attr("cx", (r: any) => x(r.d))
        .attr("cy", (r: any) => y(r.v))
        .attr("r", 3)
        .attr("fill", COLORS.red)
        .on("mousemove", (ev: MouseEvent, r: any) =>
          tip.show(
            `<b>${fmtMonthLong(r.d)}</b><br>${r.v >= 0 ? "Ahead of" : "Behind"} Target by <b>${Math.abs(Math.round(r.v))}</b>`,
            ev,
          ),
        )
        .on("mouseleave", () => tip.hide());
    },
    [rows, height],
  );
  const { ref, svgRef, width } = useD3Chart(draw);

  return (
    <div
      ref={ref}
      style={{ position: "relative", width: "100%", fontFamily: FONT }}
    >
      <svg ref={svgRef} width={width} height={height} />
    </div>
  );
}
